/**
 * Message Breakdown Dialog - Shows detailed message structure of a verified entry
 * Displays which messages are TITLE, SEPARATOR, or MEDIA with color-coded badges
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as TitleIcon,
  Straighten as SeparatorIcon,
  Collections as MediaIcon,
} from '@mui/icons-material';
import { VerifiedEntry, Message, EntryMetadata } from '@/types/api';
import { getMessagesBatch } from '@/api/messages';
import ErrorAlert from '@/components/common/ErrorAlert';

interface MessageBreakdownDialogProps {
  open: boolean;
  entry: VerifiedEntry;
  chatId: number;
  onClose: () => void;
}

interface MessageWithType {
  message: Message;
  type: 'TITLE' | 'SEPARATOR' | 'MEDIA';
  mediaId?: number;
}

const MessageBreakdownDialog: React.FC<MessageBreakdownDialogProps> = ({
  open,
  entry,
  chatId,
  onClose,
}) => {
  const { t } = useTranslation('components');
  const [messages, setMessages] = useState<Record<number, Message> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setMessages(null);
      setError(null);
      return;
    }

    const loadMessages = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const metadata: EntryMetadata | null = entry.metadata;
        const messageIds: number[] = [entry.title_message_id];

        // Add separator if it exists
        if (metadata?.separator_message_id) {
          messageIds.push(metadata.separator_message_id);
        }

        // Add media message IDs
        if (metadata?.media_message_ids) {
          messageIds.push(...metadata.media_message_ids);
        } else if (entry.media_links) {
          // Fallback to media_links if metadata is not available
          messageIds.push(...entry.media_links.map((ml) => ml.source_message_id));
        }

        const result = await getMessagesBatch(chatId, messageIds);
        setMessages(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load messages');
      } finally {
        setIsLoading(false);
      }
    };

    loadMessages();
  }, [open, entry, chatId]);

  const metadata: EntryMetadata | null = entry.metadata;
  const messageList: MessageWithType[] = [];

  // Build structured message list
  if (messages) {
    // Title message
    if (messages[entry.title_message_id]) {
      messageList.push({
        message: messages[entry.title_message_id],
        type: 'TITLE',
      });
    }

    // Separator message
    if (metadata?.separator_message_id && messages[metadata.separator_message_id]) {
      messageList.push({
        message: messages[metadata.separator_message_id],
        type: 'SEPARATOR',
      });
    }

    // Media messages
    const mediaIds = metadata?.media_message_ids ||
                     entry.media_links?.map((ml) => ml.source_message_id) || [];
    mediaIds.forEach((msgId, index) => {
      if (messages[msgId]) {
        const mediaId = metadata?.media_message_ids
          ? entry.media_links?.[index]?.media_id
          : entry.media_links?.[entry.media_links.findIndex(ml => ml.source_message_id === msgId)]?.media_id;

        messageList.push({
          message: messages[msgId],
          type: 'MEDIA',
          mediaId,
        });
      }
    });
  }

  const getTelegramLink = (messageId: number) => {
    // Link format: https://t.me/c/{chat_id}/{message_id}
    // For supergroup use: c/{abs(chat_id) - 1000000000000}
    const adjustedChatId = Math.abs(chatId) - 1000000000000;
    return `https://t.me/c/${adjustedChatId}/${messageId}`;
  };

  const getMessageTypeColor = (type: 'TITLE' | 'SEPARATOR' | 'MEDIA') => {
    switch (type) {
      case 'TITLE':
        return 'primary';
      case 'SEPARATOR':
        return 'warning';
      case 'MEDIA':
        return 'success';
    }
  };

  const getMessageTypeIcon = (type: 'TITLE' | 'SEPARATOR' | 'MEDIA') => {
    switch (type) {
      case 'TITLE':
        return <TitleIcon fontSize="small" />;
      case 'SEPARATOR':
        return <SeparatorIcon fontSize="small" />;
      case 'MEDIA':
        return <MediaIcon fontSize="small" />;
    }
  };

  const truncateText = (text: string | null, maxLength: number = 100) => {
    if (!text) return '(no text)';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Translate message type label
  const getMessageTypeLabel = (type: 'TITLE' | 'SEPARATOR' | 'MEDIA'): string => {
    return t(`customPatternBuilder.patternElementTypes.${type}`, type);
  };

  // Translate full pattern
  const translatePattern = (pattern: string): string => {
    const parts = pattern.toUpperCase().split('_');
    const translatedParts = parts.map((part) =>
      t(`customPatternBuilder.patternElementTypes.${part}`, part)
    );
    return translatedParts.join(' → ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          <Typography variant="h6">Message Structure Details</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <ErrorAlert error={error} title="Failed to load messages" />}

        {!isLoading && !error && messageList.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {messageList.map((item, index) => {
              const { message, type, mediaId } = item;
              return (
                <Box key={`${type}-${message.message_id}`}>
                  {/* Message Type Badge */}
                  <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      icon={getMessageTypeIcon(type)}
                      label={getMessageTypeLabel(type)}
                      size="small"
                      color={getMessageTypeColor(type)}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Message #{message.message_id}
                    </Typography>
                  </Box>

                  {/* Message Content Preview */}
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ direction: 'rtl', textAlign: 'right', mb: 1 }}>
                      {truncateText(message.message_text)}
                    </Typography>

                    {/* Media Info */}
                    {type === 'MEDIA' && mediaId && (
                      <Typography variant="caption" color="text.secondary">
                        Media ID: {mediaId}
                      </Typography>
                    )}
                  </Box>

                  {/* Telegram Link */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      href={getTelegramLink(message.message_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="text"
                    >
                      Open in Telegram
                    </Button>
                  </Box>

                  {index < messageList.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
              );
            })}

            {/* Pattern Info */}
            {metadata?.pattern && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Pattern Detected
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {translatePattern(metadata.pattern)}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        {!isLoading && !error && messageList.length === 0 && (
          <Alert severity="info">No message data available</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessageBreakdownDialog;
